import clsx from 'clsx';
import {
  CSSProperties,
  FC,
  MouseEventHandler,
  PropsWithChildren,
  createElement,
  forwardRef,
} from 'react';
import styles from './typography.module.css';

type types = LG | MD | M | SM | XS;
type XL = 'xl';
type LG = 'lg';
type MD = 'md';
type SM = 'sm';
type M = 'm';
type XS = 'xs';
type Light = 'light';
type Medium = 'medium';
type Bold = 'bold';

type NumberT = { className?: string };
type Color = 'black' | 'purple' | 'white';
/**
 * font-family: inter
 *
 * mobile: 64px
 *
 * desktop: 140px
 */
export const NumberText: FC<PropsWithChildren<NumberT>> = ({
  className,
  children,
}) => {
  return <span className={clsx(styles.numberText, className)}>{children}</span>;
};

type HeadLineT = {
  className?: string;
  tag?: string;
  style?: CSSProperties;
  color?: Color;
} & (
  | { type: LG; weight: Light | Medium | Bold }
  | { type: SM; weight?: Light | Bold }
  | { type: MD }
  | { type: XS; weight?: Bold }
  | { type: M }
);

/**
 * font-family: raleway
 *
 * mobile: lg:56px; md:48px; m:40px; sm:32px; xs:24px
 *
 * desktop: lg:80px; md:64px; m:56px; sm:40px; xs:32px
 */
export const HeadLine: FC<PropsWithChildren<HeadLineT>> = (props) => {
  const hMap: Record<types, string> = {
    lg: 'h1',
    md: 'h2',
    sm: 'h3',
    xs: 'h4',
    m: 'h5',
  };
  const hTag = hMap[props.type];

  return createElement(
    props.tag || hTag,
    {
      className: clsx(
        styles.headline,
        styles[`headline_${props.type}`],
        'weight' in props && props.weight
          ? styles[props.weight]
          : styles.regular,
        props?.color && styles[props.color],
        props.className,
      ),
      ...(props?.style && { style: props.style }),
    },
    props.children,
  );
};

type TitleT = {
  className?: string;
  type: LG | MD;
  tag?: string;
  style?: CSSProperties;
  color?: Color;
};
/**
 * font-family: raleway
 *
 * mobile: lg:20px; md:18px
 *
 * desktop: lg:20px; md:18px
 */
export const Title: FC<PropsWithChildren<TitleT>> = ({
  type,
  className,
  children,
  tag,
  style,
  color,
}) => {
  return createElement(
    tag || 'p',
    {
      className: clsx(
        styles.title,
        styles[`title_${type}`],
        styles.bold,
        color && styles[color],
        className,
      ),
      style,
    },
    children,
  );
};

type LeadT = { className?: string };
/**
 * font-family: inter
 *
 * mobile: 16px
 *
 * desktop: 20px
 */
export const Lead: FC<PropsWithChildren<LeadT>> = ({ className, children }) => {
  return (
    <p className={clsx(styles.lead, styles.medium, className)}>{children}</p>
  );
};

type OverLineT = { className?: string; type: MD };
/**
 * font-family: inter
 *
 * mobile: 16px
 *
 * desktop: 18px
 */
export const OverLine: FC<PropsWithChildren<OverLineT>> = ({
  className,
  children,
  type,
}) => {
  return (
    <p className={clsx(styles.overline, styles[`overline_${type}`], className)}>
      {children}
    </p>
  );
};

type BodyT = {
  tag?: string;
  className?: string;
  innerHTML?: string;
  style?: CSSProperties;
} & (
  | { type: XL; weight?: Medium }
  | { type: LG; weight?: Medium }
  | { type: MD; weight?: Medium | Bold }
  | { type: SM; weight?: Medium | Bold }
);
/**
 * font-family: inter
 *
 * mobile: lg:16px; md:16px; sm:14px
 *
 * desktop: lg:18px; md:16px; sm:14px
 */
export const Body: FC<PropsWithChildren<BodyT>> = (props) => {
  return createElement(
    props.tag || 'p',
    {
      className: clsx(
        styles.body,
        styles[`body_${props.type}`],
        'weight' in props && props.weight && styles[props.weight],
        props.className,
      ),
      dangerouslySetInnerHTML: props.innerHTML
        ? { __html: props.innerHTML }
        : undefined,
      ...(props.style && { style: props.style }),
    },
    props.children,
  );
};

type CTA_T = {
  className?: string;
  type: LG | MD;
  tag?: string;
  onClick?: MouseEventHandler<HTMLSpanElement | HTMLParagraphElement>;
};
/**
 * font-family: inter
 *
 * mobile: lg:18px; md:16px
 *
 * desktop: lg:20px; md:16px
 */
export const CTA: FC<PropsWithChildren<CTA_T>> = ({
  children,
  className,
  type,
  onClick,
  tag = 'span',
}) => {
  return createElement(
    tag,
    {
      className: clsx(styles.cta, styles[`cta_${type}`], className),
      onClick,
    },
    children,
  );
};

type PriceT = { className?: string; type: LG | MD | SM };
/**
 * font-family: inter
 *
 * font-size: lg:32px; md:20px; sm:16px
 */
export const Price: FC<PropsWithChildren<PriceT>> = ({
  className,
  type,
  children,
}) => {
  return (
    <span className={clsx(styles.price, styles[`price_${type}`], className)}>
      {children}
    </span>
  );
};

type InfoT = { className?: string; onClick?: () => void };
/**
 * font-family: inter
 *
 * font-size: 12px
 */
export const Info = forwardRef<HTMLSpanElement, PropsWithChildren<InfoT>>(
  /** forwardRef and ...props are required for correct use of the mui tooltip  */
  ({ className, onClick, children, ...props }, ref) => {
    return (
      <span
        className={clsx(styles.info, className)}
        onClick={onClick}
        ref={ref}
        {...props}
      >
        {children}
      </span>
    );
  },
);
